import FButtonPrevious from 'components/BaseComponents/FButton/FPreviousButton'
import SBox from 'components/opinionless/SBox'
import SFlex from 'components/opinionless/SFlex'
import SInputError from 'components/opinionless/SInputErrorV2'
import SText from 'components/opinionless/SText'
import { WIZARD_STEPS_MAPPING } from 'containers/Apply/LongBeachWizard'
import { updatePercentageAndStep } from 'v2/components/XProgressBar/utils'
import { LBButton, LBText } from 'containers/Apply/steps/ApplicationStepLBIntroduction'
import { useTranslation } from 'i18n'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { StepWizardChildProps } from 'react-step-wizard'
import STheme, { SStyled, useSTheme } from 'styles/theme'
import { useSaveHouseholdMembersMutation, useUpdateBlindDisabledMutation } from 'types/graphql'
import { ga4_useTitle } from 'utils/ga4'
import XBanner from 'v2/components/XBanner'
import { ApplicationContext } from 'v2/context/application'
import { useApplicationAnswers } from 'v2/hooks/useApplicationAnswers'
import { useApplicationBasicInfo } from 'v2/hooks/useApplicationBasicInfo'
import { useHouseholdMembers } from 'v2/hooks/useHouseholdMembers'
import { SRadioLabelV2, SRadioV2 } from '../../../Apply/steps/ApplicationStepLBRemainingQuestions'

interface Props {
    setCurrentStep?: (number) => void
    onSaveAndContinue: (string) => void
    applicationId: string
    personId: string
    fontFamily: any
    onNextStep?: any
    onPrevious?: any
    previousSelection?: boolean
    updatePercentage: any
}

export const Container = SStyled(SBox)`
    width: 100%;
    max-width: 600px;
`
const InputRow = SStyled(SFlex)`
    flex-direction: row;
    margin-top: 24px;

    ${(p) => `
        @media screen and (max-width: ${p.theme.breakpoints[0]}) {
            flex-direction: column;
        }
    `}
`

const HouseholdIncomeBlindDisabledChoice: React.FC<Props> = (props: Props & StepWizardChildProps) => {
    const applicationContext = useContext(ApplicationContext)

    const {
        loading: householdMembersLoading,
        error: householdMembersError,
        refetch: householdMembersRefetch,
    } = useHouseholdMembers()
    useApplicationBasicInfo()
    const {
        loading: applicationAnswersLoading,
        refetch: refetchApplicationAnswers,
        getAnswersByCategory,
    } = useApplicationAnswers()

    useEffect(() => {
        refetchApplicationAnswers()
    }, [refetchApplicationAnswers])

    useEffect(() => {
        householdMembersRefetch()
    }, [householdMembersRefetch])

    const theme = useSTheme()
    const { t } = useTranslation(window.LANG_NS)
    const { handleSubmit, errors, setError, clearErrors, register, getValues, setValue } = useForm()
    const [householdMembersDetails, setHouseholdMembersDetails] = useState([])
    const [updateBlindDisabled, { loading: updateBlindDisabledLoading }] = useUpdateBlindDisabledMutation()
    const [saveHouseholdMembers, { loading: saveHouseholdMembersLoading }] = useSaveHouseholdMembersMutation()
    const [blindDisabledChoiceBtn, setBlindDisabledChoiceBtn] = useState(null)

    const fontFamily = props.fontFamily

    const [loading, setLoading] = useState(true)

    ga4_useTitle('Household Income Blind Disabled Choice')

    useEffect(() => {
        if (!householdMembersLoading) {
            setLoading(false)
            setHouseholdMembersDetails(
                applicationContext.householdMembers.map((member) => ({
                    id: member.id,
                    name: member.name,
                    relation: member.relation,
                    dob: member.dob,
                    blindDisabled: member.blindDisabled,
                }))
            )
        }
    }, [applicationContext.householdMembers])

    const handleBlindDisabledNoSelection = () => {
        const updatedHouseholdMembers = householdMembersDetails.map((member) => ({
            ...member,
            blindDisabled: false,
        }))

        updateBlindDisabled({
            variables: {
                verificationCode: applicationContext.profile.person.id,
                blindDisabled: false,
            },
        }).then((response) => {
            if (response.data.updateBlindDisabled.ok === true) {
                saveHouseholdMembers({
                    variables: {
                        personId: applicationContext.profile.person.id,
                        householdMembers: updatedHouseholdMembers,
                    },
                }).then((response) => {
                    if (!response.data.saveHouseholdMembers.success) {
                        setError('householdMembersBlindDisabledChoice', { type: 'oops', message: t('error') })
                    }
                })
            } else {
                setError('householdMembersBlindDisabledChoice', { type: 'oops', message: t('error') })
            }
        })
    }

    const onSubmit = () => {
        props.onSaveAndContinue(true)
        if (getValues('long_beach_household_income_blind_disabled_choice') === 'blind_disabled_no') {
            props.onSaveAndContinue(false)
            handleBlindDisabledNoSelection()
        }

        props.nextStep()
    }

    const handleBlindDisabledSelection = (e) => {
        setValue('long_beach_household_income_blind_disabled_choice', e.target.getAttribute('value'))
        clearErrors('long_beach_household_income_blind_disabled_choice')
        setBlindDisabledChoiceBtn(e.target.getAttribute('value'))
    }

    const blindDisabledResponse = useMemo(() => {
        if (
            applicationContext?.profile?.blindDisabled === true ||
            applicationContext.householdMembers.some(
                (member) => member.blindDisabled === true || props.previousSelection === true
            )
        ) {
            setValue('long_beach_household_income_blind_disabled_choice', 'blind_disabled_yes')
            return 'blind_disabled_yes'
        }
        if (props.previousSelection === false) {
            setValue('long_beach_household_income_blind_disabled_choice', 'blind_disabled_no')
            return 'blind_disabled_no'
        }

        return null
    }, [applicationContext.profile, applicationContext.householdMembers])

    useEffect(() => {
        if (blindDisabledResponse !== undefined) {
            setBlindDisabledChoiceBtn(blindDisabledResponse)
        }
    }, [blindDisabledResponse])

    const { percentage, wizardStep, childWizzardStep } = updatePercentageAndStep(
        WIZARD_STEPS_MAPPING.householdInfo,
        WIZARD_STEPS_MAPPING.householdInfo.blindDisabledChoice,
        WIZARD_STEPS_MAPPING
    )
    props.updatePercentage(percentage, wizardStep, childWizzardStep)

    if (loading) {
        return null
    }
    return (
        <Container>
            <SBox as="form" onSubmit={handleSubmit(onSubmit)}>
                <FButtonPrevious onClick={props.previousStep} />
                <LBText
                    sx={{
                        textAlignLast: 'left',
                        whiteSpace: 'pre-line',
                        fontFamily: fontFamily.body,
                        fontWeight: '800',
                        lineHeight: '140%',
                        color: '#202020',
                        fontSize: '16px',
                        marginBottom: '24px',
                    }}
                >
                    {t('long_beach_household_income_blind_disabled_choice_subtitle')}
                </LBText>

                <InputRow
                    sx={{ display: 'flex !important', flexDirection: 'column !important' }}
                    id="long_beach_household_income_blind_disabled_choice"
                >
                    <SRadioLabelV2
                        color={theme.colors.textColor}
                        sx={{
                            fontFamily: fontFamily.body,
                        }}
                        marginBottom={'8px'}
                    >
                        <SBox maxWidth={'35px'} marginRight={'11px'}>
                            <SRadioV2
                                name="blindDisabledChoiceYes"
                                type="radio"
                                value="blind_disabled_yes"
                                checked={blindDisabledChoiceBtn === 'blind_disabled_yes'}
                                color={
                                    blindDisabledChoiceBtn === 'blind_disabled_yes'
                                        ? theme.colors.secondaryColor
                                        : STheme.colors.grayUnselectedRadioOutline
                                }
                                onClick={handleBlindDisabledSelection}
                                {...register('long_beach_household_income_blind_disabled_choice', {
                                    required: 'required_selection' as string,
                                })}
                                size={'26px'}
                            />
                        </SBox>

                        <SText
                            sx={{
                                width: '100%',
                                textAlignLast: 'left',
                                whiteSpace: 'pre-line',
                                lineHeight: '140%',
                                fontWeight: '400',
                                fontSize: '14px',
                                color: theme.colors.gray400,
                                fontFamily: fontFamily.body,
                            }}
                            dangerouslySetInnerHTML={{
                                __html: t('long_beach_household_income_blind_disabled_yes'),
                            }}
                        />
                    </SRadioLabelV2>
                    <SRadioLabelV2
                        color={theme.colors.textColor}
                        sx={{
                            fontFamily: fontFamily.body,
                        }}
                        marginBottom={'8px'}
                    >
                        <SBox maxWidth={'35px'} marginRight={'11px'}>
                            <SRadioV2
                                name="blindDisabledChoiceNo"
                                type="radio"
                                value="blind_disabled_no"
                                checked={blindDisabledChoiceBtn === 'blind_disabled_no'}
                                color={
                                    blindDisabledChoiceBtn === 'blind_disabled_no'
                                        ? theme.colors.secondaryColor
                                        : STheme.colors.grayUnselectedRadioOutline
                                }
                                onClick={handleBlindDisabledSelection}
                                {...register('long_beach_household_income_blind_disabled_choice', {
                                    required: 'required_selection' as string,
                                })}
                                size={'26px'}
                            />
                        </SBox>

                        <SText
                            sx={{
                                width: '100%',
                                textAlignLast: 'left',
                                whiteSpace: 'pre-line',
                                lineHeight: '140%',
                                fontWeight: '400',
                                fontSize: '14px',
                                color: theme.colors.gray400,
                                fontFamily: fontFamily.body,
                            }}
                            dangerouslySetInnerHTML={{
                                __html: t('long_beach_household_income_blind_disabled_no'),
                            }}
                        />
                    </SRadioLabelV2>
                </InputRow>
                <SInputError
                    forElementId={'long_beach_household_income_blind_disabled_choice'}
                    sx={{
                        fontFamily: fontFamily.body,
                        color: theme.colors.redError,
                    }}
                >
                    {t(errors.long_beach_household_income_blind_disabled_choice?.message)}
                </SInputError>

                <LBButton
                    type={'button'}
                    id={'continueButton'}
                    loading={updateBlindDisabledLoading || saveHouseholdMembersLoading || false}
                    onClick={handleSubmit(onSubmit)}
                    title={t('save_and_continue')}
                    width={'100%'}
                    mt={7}
                    mb={5}
                    textStyle={{
                        fontWeight: 700,
                        fontSize: '14px',
                        lineHeight: '140%',
                        fontFamily: fontFamily.body,
                        color: theme.colors.white,
                    }}
                >
                    {t('save_and_continue')}
                </LBButton>
                {errors.householdMembersBlindDisabledChoice && (
                    <SInputError
                        sx={{
                            fontFamily: fontFamily.body,
                            color: theme.colors.redError,
                        }}
                        forElementId={'continueButton'}
                    >
                        {t(errors.householdMembersBlindDisabledChoice?.message)}
                    </SInputError>
                )}
            </SBox>
        </Container>
    )
}

export default HouseholdIncomeBlindDisabledChoice
